/**
 * The one sanctioned crack in the frozen-base invariant — `edit_combatant_base` (which
 * re-clamps `current` into the new bounds) and its lossy-safe internal inverse.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { ok } from "@lancer-vtt/shared";
import { changed, clamp, requireCombatant, withCombatant, type EncounterHandler } from "./context";

export const applyEditCombatantBase: EncounterHandler<"edit_combatant_base"> = (state, cmd) => {
  // Replace the combatant's frozen base wholesale (the one sanctioned crack in
  // the frozen-base invariant — see commands.ts). Re-clamp
  // `current` into the new bounds: shrinking maxHp below current hp is legal (the
  // edit lops the excess); a base that drops a track (`maxHeat`/`maxStructure`/
  // `maxStress` omitted) clears the matching `current` field to ABSENT — no `0`
  // placeholder, mirroring the base field's own presence/absence. Capture
  // BOTH prior base + prior current so the inverse restores exactly what the
  // re-clamp may have changed (a plain edit back can't, since it would re-clamp
  // again).
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const prevBase = c.base;
  const prevCurrent = c.current;
  const nextBase = cmd.base;
  const hp = clamp(c.current.hp, 0, nextBase.maxHp);
  const { heat: _droppedHeat, structure: _droppedStructure, stress: _droppedStress, ...currentWithoutTracks } =
    c.current;
  const nextCurrent = {
    ...currentWithoutTracks,
    hp,
    ...(nextBase.maxHeat !== undefined ? { heat: clamp(c.current.heat ?? 0, 0, nextBase.maxHeat) } : {}),
    ...(nextBase.maxStructure !== undefined
      ? { structure: clamp(c.current.structure ?? 0, 0, nextBase.maxStructure) }
      : {}),
    ...(nextBase.maxStress !== undefined
      ? { stress: clamp(c.current.stress ?? 0, 0, nextBase.maxStress) }
      : {}),
  };
  const next = withCombatant(state, {
    ...c,
    base: nextBase,
    current: nextCurrent,
  });
  return ok({
    state: next,
    inverse: {
      type: "restore_combatant_base",
      actorId: cmd.actorId,
      combatantId: c.id,
      base: prevBase,
      current: prevCurrent,
    },
    events: changed(c.id),
  });
};

export const applyRestoreCombatantBase: EncounterHandler<"restore_combatant_base"> = (state, cmd) => {
  // Internal inverse of edit_combatant_base: set base + current directly to the
  // captured values (no re-clamp — it only ever replays a state the encounter was
  // already in, so it round-trips exactly). Its own inverse captures the current
  // base+current so undo/redo of a base edit round-trips both ways.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const prevBase = c.base;
  const prevCurrent = c.current;
  const next = withCombatant(state, { ...c, base: cmd.base, current: cmd.current });
  return ok({
    state: next,
    inverse: {
      type: "restore_combatant_base",
      actorId: cmd.actorId,
      combatantId: c.id,
      base: prevBase,
      current: prevCurrent,
    },
    events: changed(c.id),
  });
};
